import React, { useCallback } from "react";
import { Space, Input, Select, Button } from "antd";
import { DatePicker } from "../../../components/antOverrides";
import moment from "moment";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { setDate } from "../dayCenterSlice";
import { PrinterOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import { useParams, useLocation } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import { listDayCenterReport } from "../../../api/server";
import { setHeader } from "../../../components/print/printSections/reports";
import { getPdfData } from "./getPdfData";
import { getReportPdf } from "../../../components/util/helpers";
import { jsPDF } from "jspdf";

const { Option } = Select;

/**
 * Header component for the Day Center feature
 * @param {Object} props - Component props
 * @param {Function} props.onBranchChange - Callback for branch selection change
 * @param {number|string} props.branch_id - Selected branch ID
 * @param {Function} props.onTextChange - Callback for text input change
 * @returns {JSX.Element} Header component
 */
const Header = (props) => {
  const { onBranchChange, branch_id, onTextChange } = props;
  let location = useLocation();
  const intl = useIntl();
  const activeDate = useSelector((state) => state.dayCenter.activeDate);
  const activeBranch = useSelector((state) => state.dayCenter.activeBranch);
  const dayCenterPage = useSelector((state) => state.dayCenter.page);
  const profile = useSelector((state) => state.user.profile);
  const { filter } = dayCenterPage;
  const dispatch = useDispatch();
  const isAdmin = location.pathname.includes("admin");
  let { branchId } = useParams();
  const branch = branchId ? branchId : activeBranch;

  const onDateChange = (val) => {
    dispatch(setDate({ date: moment(val).format() }));
  };

  /**
   * Generates and opens a PDF report
   * @param {string} action - Action type ('save' or 'print')
   */
  const onGenereatePdf = useCallback(
    async (action) => {
      const orientation = "l";
      const selectedMonth = moment(activeDate);
      const from = selectedMonth.startOf("month").format();
      const to = selectedMonth.endOf("month").format();
      const scopePrefix = branch ? `branches/${branch}/` : "";

      const params = {
        scopePrefix,
        query: {
          from,
          to,
        },
      };

      const result = await listDayCenterReport(params);
      // Sort patients by surname and then by client_catgory_name
      const sortedPatients = (result?.data?.patients || []).sort((a, b) => {
        const surnameComparison = a.surename.localeCompare(b.surename);
        if (surnameComparison !== 0) return surnameComparison;
        return a.client_catgory_name.localeCompare(b.client_catgory_name);
      });

      // Group patients by client_catgory_name
      const groupedPatients = sortedPatients.reduce((acc, patient) => {
        const category = patient.client_catgory_name;
        if (!acc[category]) acc[category] = [];
        acc[category].push(patient);
        return acc;
      }, {});

      // Create a single jsPDF instance for all groups
      let mergedPdf = new jsPDF({
        orientation: orientation,
      });

      // Generate the PDF report for each category
      Object.entries(groupedPatients).forEach(([category, patients], index) => {
        if (index > 0) {
          mergedPdf.addPage();
          const last = mergedPdf.getNumberOfPages();
          mergedPdf.setPage(last);
        }

        const pdfData = getPdfData(patients, activeDate);
        const headerRecord = {
          name: `דוח נוכחות מרכז יום לחודש ${moment(activeDate).format(
            "MM/YYYY"
          )} - ${category}`,
        };

        mergedPdf = getReportPdf(
          "",
          pdfData,
          setHeader,
          headerRecord,
          null,
          null,
          pdfData,
          "paging",
          40,
          intl,
          "analytics",
          {
            styles: {
              font: "Rubik",
              halign: "right",
              overflow: "linebreak",
              lineWidth: 0.1,
              lineColor: 0,
            },
            headStyles: {
              fillColor: [220, 220, 220],
              textColor: 20,
              lineWidth: 0.1,
              lineColor: 0,
            },
            bodyStyles: {
              lineWidth: 0.1,
              lineColor: 0,
            },
            footStyles: {
              fillColor: [220, 220, 220],
              textColor: 20,
              lineWidth: 0.1,
              lineColor: 0,
            },
            margin: { top: 26 },
          },
          null,
          mergedPdf
        );
      });

      // Save the PDF as a file for download
      if (action === "save") {
        const pdfBlob = mergedPdf.output("blob");
        const fileName = `DayCenterReport_${moment(activeDate).format(
          "MMYYYY"
        )}.pdf`;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(pdfBlob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      } else if (action === "print") {
        // Open the PDF in a new tab for printing
        window.open(mergedPdf.output("bloburl"), "_blank");
      }
    },
    [activeDate, branch, intl]
  );

  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Space direction="horizontal">
        <DatePicker
          value={activeDate && moment(activeDate)}
          onChange={onDateChange}
          format={"DD/MM/YYYY"}
          placeholder={intl.formatMessage({
            id: `app.gaps.select_month`,
          })}
          allowClear={false}
        />
        {isAdmin ? (
          <Select
            style={{ width: "200px" }}
            defaultValue={branchId ? parseInt(branchId) : undefined}
            showSearch
            placeholder={intl.formatMessage({
              id: "app.users.chooseBranch",
            })}
            optionFilterProp="children"
            onChange={onBranchChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            value={branch_id}
            allowClear
          >
            {profile.branches
              ? profile.branches.map((b) => (
                  <Option value={b.id} key={b.id}>
                    {b.name}
                  </Option>
                ))
              : null}
          </Select>
        ) : null}
        <Input
          prefix={
            <SearchOutlined style={{ padding: "0  5px", opacity: 0.4 }} />
          }
          placeholder={intl.formatMessage({
            id: `app.patients.typepatientsName`,
          })}
          value={filter.free_text}
          onChange={(e) => onTextChange(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </Space>

      <Button
        icon={<PrinterOutlined />}
        onClick={() => onGenereatePdf("print")}
        disabled={!activeBranch && !branchId}
      >
        {" "}
        {<FormattedMessage id="app.actions.print_monthly_report" />}
      </Button>
    </div>
  );
};

export default Header;
